import FeatherIcon from "feather-icons-react";
import React, { useState } from "react";
import {
  Button,
  Col,
  FloatingLabel,
  Form,
  Row,
  ProgressBar
} from "react-bootstrap";
import { CopyToClipboard } from "react-copy-to-clipboard";
import zxcvbn from "zxcvbn";
import { generatePassword, generatePassphrase } from "../services/PasswordGeneratorUtil";
import CopyButton from "./CopyButton";

function PasswordGenerator() {
  const [usePhrasePassword, setUsePhrasePassword] = useState(false);
  const [useNumbers, setUseNumbers] = useState(true);
  const [useSymbols, setUseSymbols] = useState(true);
  const [useUppercase, setUseUppercase] = useState(true);
  const [passwordLength, setPasswordLength] = useState(12);
  const [numWords, setNumWords] = useState(4);
  const [wordSeparator, setWordSeparator] = useState("-");
  const [password, setPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);

  const handleUsePhrasePasswordChange = (e) => {
    setUsePhrasePassword(e.target.checked);
  };

  const handleUseNumbersChange = (e) => {
    setUseNumbers(e.target.checked);
  };

  const handleUseSymbolsChange = (e) => {
    setUseSymbols(e.target.checked);
  };

  const handleUseUppercaseChange = (e) => {
    setUseUppercase(e.target.checked);
  };

  const handlePasswordLengthChange = (e) => {
    setPasswordLength(e.target.value);
  };

  const handleNumWordsChange = (e) => {
    setNumWords(e.target.value);
  };

  const handleWordSeparatorChange = (e) => {
    setWordSeparator(e.target.value);
  };

  function generateSecurePassword() {
    let passwordToReturn;
    if (usePhrasePassword) {
      passwordToReturn = generatePassphrase(numWords, wordSeparator);
    } else {
      passwordToReturn = generatePassword(
        useUppercase,
        useNumbers,
        useSymbols,
        passwordLength
      );
    }
    setPassword(passwordToReturn);
    setPasswordStrength(zxcvbn(passwordToReturn).score);
  }

  const getProgressBarVariant = (strength) => {
    if (strength === 0 || strength === 1) return "danger";
    if (strength === 2) return "warning";
    if (strength === 3 || strength === 4) return "success";
  };

  return (
    <div>
      <Row>
        <Col md="6" className="h-100 mb-3">
          <Form>
            <Form.Group className="mb-3">
              <Form.Check
                checked={usePhrasePassword}
                onChange={handleUsePhrasePasswordChange}
                className="d-inline me-2"
              />
              <Form.Label className="d-inline">
                Enable Phrase Password
              </Form.Label>
            </Form.Group>

            {usePhrasePassword ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Number of Words</Form.Label>
                  <Form.Control
                    type="number"
                    value={numWords}
                    onChange={handleNumWordsChange}
                    min="1"
                    max="10"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Word Separator</Form.Label>
                  <Form.Control
                    type="text"
                    value={wordSeparator}
                    onChange={handleWordSeparatorChange}
                  />
                </Form.Group>
              </>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Check
                    checked={useNumbers}
                    onChange={handleUseNumbersChange}
                    className="d-inline me-2"
                  />
                  <Form.Label className="d-inline">
                    Include Numbers
                  </Form.Label>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    checked={useSymbols}
                    onChange={handleUseSymbolsChange}
                    className="d-inline me-2"
                  />
                  <Form.Label className="d-inline">
                    Include Symbols
                  </Form.Label>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    checked={useUppercase}
                    onChange={handleUseUppercaseChange}
                    className="d-inline me-2"
                  />
                  <Form.Label className="d-inline">
                    Include Uppercase Letters
                  </Form.Label>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Password Length</Form.Label>
                  <Form.Control
                    type="number"
                    value={passwordLength}
                    onChange={handlePasswordLengthChange}
                    min="1"
                    max="128"
                  />
                </Form.Group>
              </>
            )}

            <Button
              variant="dark"
              type="button"
              onClick={generateSecurePassword}
              className="mt-3 me-2"
            >
              Generate
            </Button>
            <Button variant="outline-dark" type="reset" className="mt-3">
              Reset
            </Button>
          </Form>
        </Col>
        <Col className="order-md-last order-first mb-3">
          <FloatingLabel controlId="floatingTextarea2" label="Output">
            <Form.Control
              value={password}
              placeholder="output"
              style={{ height: "100%" }}
              readOnly
            />
          </FloatingLabel>
          <CopyButton inputText={password} />
          <ProgressBar
            now={passwordStrength * 25}
            label={`${passwordStrength === 0 || passwordStrength === 1 ? 'Weak' : passwordStrength === 2 ? 'Average' : 'Strong'}`}
            variant={getProgressBarVariant(passwordStrength)}
            className="mt-3"
          />
        </Col>
      </Row>
    </div>
  );
}

export default PasswordGenerator;
